import { useState, useCallback, memo, lazy, Suspense } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import DashboardIcon from "../svgElements/DashboardIcon";
import LogoutIcon from "../svgElements/LogoutIcon";
import SettingsIcon from "../svgElements/SettingsIcon";
import { ROUTES } from "../../constants/routes";
import { removeAuthToken } from "../../utils/helper";
import { logout } from "../../services/auth";
import { addSentryBreadcrumb } from "../../utils/sentry";
import "../../styles/sidebar.scss";
import UserManagementIcon from "../svgElements/UserManagementIcon";
import ReportsLogsIcon from "../svgElements/ReportsLogsIcon";
const ConfirmationModal = lazy(() => import("./ConfirmationModal"));
import styles from "./Sidebar.module.css";

import KalixLogo from "../svgElements/KalixLogo";

interface SidebarProps {
  isOpen?: boolean;
  onNavigate?: () => void;
}

const Sidebar = ({ isOpen = true, onNavigate }: SidebarProps) => {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogoutClick = useCallback(() => {
    setShowLogoutModal(true);
  }, []);

  const handleLogoutConfirm = useCallback(async () => {
    try {
      setIsLoggingOut(true);
      await logout(); // Clears the HttpOnly auth cookie server-side
    } catch {
      addSentryBreadcrumb("Logout API call failed — proceeding with local logout", "auth", "warning");
    } finally {
      removeAuthToken(); // Clear user profile from localStorage
      setIsLoggingOut(false);
      setShowLogoutModal(false);
      navigate(ROUTES.LOGIN);
    }
  }, [navigate]);

  const handleLogoutCancel = useCallback(() => {
    setShowLogoutModal(false);
  }, []);

  return (
    <div className={`sidebar ${isOpen ? "is-open" : ""}`}>
      <div>
        <div className="logo">
          <KalixLogo height={56} />
        </div>
        <ul>
          <li>
            <NavLink
              to={ROUTES.DASHBOARD}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={onNavigate}
            >
              <DashboardIcon />
              Dashboard
            </NavLink>
          </li>
          <li>
            <NavLink
              to={ROUTES.USERS}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={onNavigate}
            >
              <UserManagementIcon />
              User Management
            </NavLink>
          </li>
          <li>
            <NavLink
              to={ROUTES.REPORTS_LOGS}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={onNavigate}
            >
              <ReportsLogsIcon />
              Reports & Logs
            </NavLink>
          </li>
          <li>
            <NavLink
              to={ROUTES.SETTINGS}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={onNavigate}
            >
              <SettingsIcon />
              Settings
            </NavLink>
          </li>
        </ul>
      </div>
      <ul>
        <li>
          <button type="button" onClick={handleLogoutClick} className={styles.logoutLink}>
            <LogoutIcon />
            Logout
          </button>
        </li>
      </ul>

      <Suspense fallback={null}>
        <ConfirmationModal
          isOpen={showLogoutModal}
          title="Logout from Admin Panel?"
          message="Are you sure you want to log out? You will need to sign in again to access the admin dashboard and manage users."
          confirmText="Yes, Logout"
          cancelText="Cancel"
          onConfirm={handleLogoutConfirm}
          onCancel={handleLogoutCancel}
          isLoading={isLoggingOut}
        />
      </Suspense>
    </div>
  );
};

export default memo(Sidebar);
